import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { getErrorMessage } from '../../../libs/ui-components/components/custom-post-error.handler';
import { Button, Dialog } from '@lib/ui-components';
import { DialogActions, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

interface CancelCollectionRequestProps {
  collectionRequestId: string;
  variant: 'organization' | 'client';
  afterSubmit?: () => void;
  disabled?: boolean;
}

const URL_BY_VARIANT: Record<CancelCollectionRequestProps['variant'], string> = {
  organization: '/api/usecase/collection-request/cancel-collection-request',
  client: '/api/usecase/collection-request-client/cancel-collection-request-by-client',
};

const MAX_REASON_LENGTH = 500;

export const CancelCollectionRequestButton = (props: CancelCollectionRequestProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setOpen(false);
    setReason('');
    setError(null);
  };

  const submit = async () => {
    if (!reason.trim()) {
      setError(t('fieldRequired'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await axios({
        method: 'post',
        url: URL_BY_VARIANT[props.variant],
        data: { collectionRequestId: props.collectionRequestId, reason: reason.trim() },
        responseType: 'json',
      });
      close();
      props.afterSubmit?.();
    } catch (e) {
      // backend zwraca ErrorWrapper {code, context}; komunikaty siedzą w translations.error pod tym kodem
      const translations = i18n.getDataByLanguage(i18n.language);
      const data = (e as { response?: { data?: { code?: string; context?: string } } })?.response?.data;
      setError(getErrorMessage({ code: data?.code, context: data?.context }, translations) || t('unexpectedError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button label={t('cancelOrder')} variant='contained' color='secondary' isDisabled={props.disabled} onClick={() => setOpen(true)} />
      <Dialog open={open} maxWidth='sm' fullWidth={true}>
        <DialogTitle sx={{ margin: 'auto' }}>
          {t('cancellingOrder')}
          <IconButton
            aria-label='close'
            onClick={close}
            data-test-id={'dialog-close-btn'}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {/* the floating label sits at the very top of the content, so it needs room or it rides into the title */}
        <DialogContent sx={{ paddingTop: '0.5rem !important' }}>
          <TextField
            label={t('cancellationReason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            helperText={error ?? t('cancellationReasonHint')}
            error={!!error}
            inputProps={{ maxLength: MAX_REASON_LENGTH }}
            fullWidth
            multiline
            rows={4}
            size='small'
            margin='normal'
          />
        </DialogContent>
        {/* same shape as GeneralForm's footer: centred pair, the wide padded button of every other dialog */}
        <DialogActions sx={{ justifyContent: 'center', paddingBottom: '2rem', '& button': { padding: '0.6rem 2.3rem', fontSize: '0.9rem' } }}>
          <Button label={t('cancel')} color='primary' variant='text' isDisabled={busy} onClick={close} />
          <Button label={t('confirm')} color='secondary' variant='contained' isDisabled={busy} onClick={submit} />
        </DialogActions>
      </Dialog>
    </>
  );
};
